import { GD } from "src/GD"

const DEFAULT_TRANSACTIONS_PAGE_LIMIT = 20
const ALL_TRANSACTIONS_METHOD = "companies/transactions/all"
const CARD_TRANSACTIONS_METHOD = "companies/transactions/card"

const DEFAULT_TRANSACTIONS_RESPONSE: ICompanyTransactionsResponse = {
	transactions: [],
	pagination: { total: 0, page: 0, limit: DEFAULT_TRANSACTIONS_PAGE_LIMIT, pages: 0 },
	balances: {}
}

const normalizeNullableNumber = (value: unknown) =>
	value === null || value === undefined || value === "" ? null : Number(value)

const normalizeParentTransaction = (
	parent?: ICompanyTrxParentTransaction | null
): ICompanyTrxParentTransaction | undefined =>
	parent
		? {
				...parent,
				amount: Number(parent.amount),
				operation_id: normalizeNullableNumber(parent.operation_id)
			}
		: undefined

const normalizeTransactionParty = (
	party?: ICompanyTransactionParty
): ICompanyTransactionParty | undefined =>
	party ? { ...party, account_id: Number(party.account_id) } : undefined

const normalizeTransaction = (
	transaction: ICompanyTrxTransaction
): ICompanyTrxTransaction => ({
	...transaction,
	id: Number(transaction.id),
	operation_id: normalizeNullableNumber(transaction.operation_id),
	type_id: Number(transaction.type_id),
	amount: transaction.amount,
	balance: transaction.balance,
	account_id: Number(transaction.account_id),
	counterparty_account_id: Number(transaction.counterparty_account_id),
	from: normalizeTransactionParty(transaction.from),
	to: normalizeTransactionParty(transaction.to),
	parent: normalizeParentTransaction(transaction.parent)
})

const normalizeTransactionsResponse = (
	response?: Partial<ICompanyTransactionsResponse> | null
): ICompanyTransactionsResponse => {
	if (!response) return DEFAULT_TRANSACTIONS_RESPONSE

	return {
		transactions: Array.isArray(response.transactions)
			? response.transactions.map((transaction) =>
					normalizeTransaction(transaction as ICompanyTrxTransaction)
				)
			: [],
		pagination: response.pagination
			? {
					total: Number(response.pagination.total ?? 0),
					page: Number(response.pagination.page ?? 0),
					limit: Number(response.pagination.limit ?? DEFAULT_TRANSACTIONS_PAGE_LIMIT),
					pages: Number(response.pagination.pages ?? 0)
				}
			: DEFAULT_TRANSACTIONS_RESPONSE.pagination,
		balances: response.balances ?? {},
		totals: response.totals ?? {},
		truncated: Boolean(response.truncated)
	}
}

export class CompanyTransactionsService {
	constructor() {
		// Legacy list signals share the same default scope; only callers choose entity.
		GD.REQ_COMPANY_TRANSACTIONS.listener = (filters) =>
			this.getCompanyTransactionsAll(filters as ICompanyTransactionFilter | undefined)
		GD.REQ_COMPANY_TRANSACTIONS_CARDS.listener = (filters) =>
			this.getCompanyTransactionsAll(filters as ICompanyTransactionFilter | undefined)
		GD.REQ_COMPANY_TRANSACTIONS_ALL.listener = (filters) =>
			this.getCompanyTransactionsAll(filters as ICompanyTransactionFilter | undefined)
		GD.REQ_COMPANY_TRANSACTION_FILTERS.listener = () => this.getTransactionFilters()
		GD.REQ_COMPANY_TRANSACTION_PRESETS.listener = () => this.getTransactionPresets()
		GD.REQ_COMPANY_TRANSACTION_PRESET_CREATE.listener = (data) =>
			this.createTransactionPreset(data as ITransactionFilterPresetWrite)
		GD.REQ_COMPANY_TRANSACTION_PRESET_UPDATE.listener = (data) =>
			this.updateTransactionPreset(data as ITransactionFilterPresetUpdate)
		GD.REQ_COMPANY_TRANSACTION_PRESET_DELETE.listener = (data) =>
			this.deleteTransactionPreset(data as { uid: string })
		GD.REQ_COMPANY_TRANSACTION_ACCOUNT.listener = (data) =>
			this.getTransactionAccount(data as { account_id: number })
		GD.REQ_COMPANY_TRANSACTION_DETAILS.listener = (data) =>
			this.getTransactionDetails(data as { operation_id: number })
		GD.REQ_COMPANY_TRANSACTIONS_CARD.listener = (data) =>
			this.getCardTransactions(data as { card_uid: string } & ICompanyTransactionFilter)
		GD.REQ_COMPANY_TRANSACTIONS_BUYER.listener = (data) =>
			this.getBuyerTransactions(data as { user_uid: string } & ICompanyTransactionFilter)
		GD.REQ_STATS_SPEND_OVERVIEW.listener = (params) =>
			this.getSpendOverview(params as IStatsOverviewParams | undefined)
		GD.REQ_STATS_CARDS_SPEND_OVERVIEW.listener = (params) =>
			this.getCardsSpendOverview(params as IStatsOverviewParams | undefined)
		GD.REQ_STATS_OVERVIEW.listener = (params) =>
			this.getStatsOverview(params as IStatsOverviewParams | undefined)
		GD.REQ_STATS_BY_CARD.listener = (params) =>
			this.getStatsByCard(params as IStatsOverviewParams | undefined)
		GD.REQ_STATS_TEAMS_MONTH.listener = (params) =>
			this.getTeamsMonth(params as IStatsOverviewParams | undefined)
		GD.REQ_STATS_TOP_TEAMS.listener = (params) =>
			this.getTopTeams(
				params as (IStatsOverviewParams & { limit?: number }) | undefined
			)
		GD.REQ_STATS_TOP_BUYERS.listener = (params) =>
			this.getTopBuyers(
				params as (IStatsOverviewParams & { limit?: number }) | undefined
			)
		GD.REQ_STATS_DECLINED_BY_TEAM.listener = (params) =>
			this.getDeclinedByTeam(params as { days?: number; team_uid?: string } | undefined)
		GD.REQ_STATS_DECLINED_BY_BUYER.listener = (params) =>
			this.getDeclinedByBuyer(
				params as { days?: number; buyer_uid?: string; team_uid?: string } | undefined
			)
		GD.REQ_CARD_TRANSACTIONS.listener = (data) =>
			this.getLegacyCardTransactions(data as { uid: string; card_holder_uid: string })
	}

	private requestTransactions = async (
		method: string,
		data?: unknown
	): Promise<IAPIResponse<ICompanyTransactionsResponse>> => {
		const response = await GD.REQ_API.request({
			method,
			httpMethod: "POST",
			data: data ?? {}
		})

		if (response.error) return { error: response.error }

		return {
			data: normalizeTransactionsResponse(response.data as ICompanyTransactionsResponse)
		}
	}

	private getCompanyTransactionsAll = async (
		filters?: ICompanyTransactionFilter
	): Promise<IAPIResponse<ICompanyTransactionsResponse>> =>
		this.requestTransactions(ALL_TRANSACTIONS_METHOD, filters)

	private getTransactionFilters = async (): Promise<
		IAPIResponse<ICompanyTransactionFiltersResponse>
	> =>
		(await GD.REQ_API.request({
			method: "companies/transactions/filters",
			httpMethod: "POST",
			data: {}
		})) as IAPIResponse<ICompanyTransactionFiltersResponse>

	private requestTransactionPreset = async <T>(
		method: string,
		data: unknown = {}
	): Promise<IAPIResponse<T>> =>
		(await GD.REQ_API.request({ method, httpMethod: "POST", data })) as IAPIResponse<T>

	private getTransactionPresets = async (): Promise<IAPIResponse<ITransactionFilterPreset[]>> =>
		this.requestTransactionPreset("companies/transactions/presets")

	private createTransactionPreset = async (
		data: ITransactionFilterPresetWrite
	): Promise<IAPIResponse<ITransactionFilterPreset>> =>
		this.requestTransactionPreset("companies/transactions/presets/create", data)

	private updateTransactionPreset = async (
		data: ITransactionFilterPresetUpdate
	): Promise<IAPIResponse<ITransactionFilterPreset>> =>
		this.requestTransactionPreset("companies/transactions/presets/update", data)

	private deleteTransactionPreset = async (
		data: { uid: string }
	): Promise<IAPIResponse<IStatusResponse>> =>
		this.requestTransactionPreset("companies/transactions/presets/delete", data)

	private getTransactionDetails = async (data: { operation_id: number }) =>
		(await GD.REQ_API.request({
			method: "companies/transactions/details",
			httpMethod: "POST",
			data
		})) as IAPIResponse<import("./transactionDetails").ITransactionDetails>

	private getTransactionAccount = async (
		data: { account_id: number }
	): Promise<IAPIResponse<ICompanyTransactionAccountResponse>> =>
		(await GD.REQ_API.request({
			method: "companies/transactions/account",
			httpMethod: "POST",
			data
		})) as IAPIResponse<ICompanyTransactionAccountResponse>

	private getCardTransactions = async (
		data: { card_uid: string } & ICompanyTransactionFilter
	): Promise<IAPIResponse<ICompanyTransactionsResponse>> => {
		if (!data?.card_uid) return { data: DEFAULT_TRANSACTIONS_RESPONSE }
		return this.requestTransactions(CARD_TRANSACTIONS_METHOD, data)
	}

	private getBuyerTransactions = async (
		data: { user_uid: string } & ICompanyTransactionFilter
	): Promise<IAPIResponse<ICompanyTransactionsResponse>> => {
		if (!data?.user_uid) return { data: DEFAULT_TRANSACTIONS_RESPONSE }
		const { user_uid, ...filters } = data
		return this.requestTransactions(ALL_TRANSACTIONS_METHOD, {
			...filters,
			buyer_uid: user_uid
		})
	}

	private getSpendOverview = async (
		params?: IStatsOverviewParams
	): Promise<IAPIResponse<ISpendOverviewStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/spend-overview",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<ISpendOverviewStats>
	}

	private getCardsSpendOverview = async (
		params?: IStatsOverviewParams
	): Promise<IAPIResponse<ISpendOverviewStats>> =>
		(await GD.REQ_API.request({
			method: "companies/stats/cards-spend-overview",
			httpMethod: "POST",
			data: params ?? {}
		})) as IAPIResponse<ISpendOverviewStats>

	private getStatsOverview = async (
		params?: IStatsOverviewParams
	): Promise<IAPIResponse<IStatsOverviewStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/overview",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<IStatsOverviewStats>
	}

	private getStatsByCard = async (
		params?: IStatsOverviewParams
	): Promise<IAPIResponse<IStatsByCardStats>> =>
		(await GD.REQ_API.request({
			method: "companies/stats/by-card",
			httpMethod: "POST",
			data: params ?? {}
		})) as IAPIResponse<IStatsByCardStats>

	private getTeamsMonth = async (
		params?: IStatsOverviewParams
	): Promise<IAPIResponse<ITeamsMonthStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/teams-month",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<ITeamsMonthStats>
	}

	private getTopTeams = async (
		params?: IStatsOverviewParams & { limit?: number }
	): Promise<IAPIResponse<ITopTeamsStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/top-teams",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<ITopTeamsStats>
	}

	private getTopBuyers = async (
		params?: IStatsOverviewParams & { limit?: number }
	): Promise<IAPIResponse<ITopBuyersStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/top-buyers",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<ITopBuyersStats>
	}

	private getDeclinedByTeam = async (params?: {
		days?: number
		team_uid?: string
	}): Promise<IAPIResponse<IDeclinedByTeamStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/declined-by-team",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<IDeclinedByTeamStats>
	}

	private getDeclinedByBuyer = async (params?: {
		days?: number
		buyer_uid?: string
		team_uid?: string
	}): Promise<IAPIResponse<IDeclinedByBuyerStats>> => {
		const response = await GD.REQ_API.request({
			method: "companies/stats/declined-by-buyer",
			httpMethod: "POST",
			data: params ?? {}
		})

		return response as IAPIResponse<IDeclinedByBuyerStats>
	}

	private getLegacyCardTransactions = async (data: {
		uid: string
		card_holder_uid: string
	}): Promise<IAPIResponse<ICardTransaction[]>> => {
		if (!data?.uid) return { data: [] }

		const response = await this.getCardTransactions({ card_uid: data.uid })
		if (response.error) return response

		return {
			data: response.data.transactions.map((transaction) => ({
				id: String(transaction.id),
				amount: transaction.amount,
				currency: transaction.currency,
				date: transaction.created_at,
				description: transaction.description || "Transaction",
				status: transaction.status,
				direction: transaction.direction,
				operation_id: transaction.operation_id ?? undefined,
				type_id: transaction.type_id,
				balance: transaction.balance,
				account_id: transaction.account_id,
				counterparty_account_id: transaction.counterparty_account_id,
				created_at: transaction.created_at,
				merchant: transaction.merchant
			}))
		}
	}
}
